import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import { motion } from 'framer-motion';
import type { BoxSizes } from 'theme/theme';

export const PropertyPanelContainer = styled(motion.div)`
  background: ${(p) => p.theme.palette.white};
  height: 100vh;
  max-width: 100%;
  z-index: 10;
  position: fixed;
  box-shadow: 0px 1px 18px 0px #0000001f;
  right: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

export const CardSubTitle = styled('h1')`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  margin: 0px;
  color: ${(p) => p.theme.palette.grey['800']};
`;

interface FormWrapperInterface {
  spaceRight?: number;
  spaceLeft?: number;
  size?: BoxSizes | number | undefined;
}

export const FormWrapper = styled('div')<FormWrapperInterface>`
  padding-top: ${(p) => p.theme.spacing(1)};
  padding-bottom: ${(p) => p.theme.spacing(1)};
  overflow: hidden;
  width: ${(p) => {
    if (typeof p.size === 'number') {
      return `${p.size}px`;
    } else if (typeof p.size === 'string') {
      return p.theme.boxSize[p.size];
    } else {
      return '100%';
    }
  }};
  padding-right: ${(p) => p.spaceRight && `${p.spaceRight}px`};
  padding-left: ${(p) => p.spaceLeft && `${p.spaceLeft}px`};
  width: ${(p) => p.spaceLeft && `auto`};
`;

interface FormContainerInterface {
  leftToRight?: boolean;
}
export const FormContainer = styled('div')<FormContainerInterface>`
  display: flex;
  justify-content: ${(p) => (p.leftToRight ? 'flex-start' : 'space-between')};
  gap: 10px;
`;

type CardLayoutProps = {
  layout: string[][];
  columns?: number;
};

export const GridLayout = styled('div')<CardLayoutProps>`
  display: grid;
  grid-row-gap: 0;
  grid-column-gap: 16px;
  grid-template-columns: repeat(${(p) => p.columns ?? 6}, 1fr);
  grid-template-rows: auto;
  align-items: center;
  ${(p) =>
    p.layout.flat().map(
      (c, i) => `        
        > :nth-child(${
          i + 1
        }) /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */ {
          grid-column: auto / span ${c};
        }
      `,
    )}
`;

export const MultipleCardColumnsContainer = styled('div')`
  display: flex;
  box-sizing: border-box;
  overflow-y: hidden;
`;

export const DeckDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
`;

export const DeckCheckboxes = styled('div')`
  display: flex;
  margin-top: ${(p) => p.theme.spacing(2)};
`;

export const DeckLabel = styled('div')`
  margin-right: 16px;
`;

export const UploadImageContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const UploadButtonWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
`;

interface UploadButtonInterface {
  component?: string;
}

export const UploadImageButton = styled(Button)<UploadButtonInterface>`
  background-color: ${(p) => p.theme.palette.primary.main};
  height: 56px;
  width: 100%;
  box-shadow: none;
`;

export const ClearImageButton = styled(Button)`
  color: ${(p) => p.theme.palette.primary.main};
  height: 56px;
  width: 100%;
  box-shadow: none;
`;

export const UploadImageTextRowContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-align: left;
`;

export const UploadImageTextColumnContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: ${(p) => p.theme.spacing(1)};
`;

export const ImageTextBold = styled('p')`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${(p) => p.theme.palette.grey['800']};
`;

export const ImageTextNormal = styled('p')`
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  color: ${(p) => p.theme.palette.grey['800']};
`;

export const EfficiencyContainer = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
`;

interface CheckBoxContainerInterface {
  indent?: boolean;
}
export const CheckBoxContainer = styled('div')<CheckBoxContainerInterface>`
  display: flex;
  flex-direction: column;
  margin-left: ${(p) => (p.indent ? `40px` : `0px`)};
`;

interface IconContainerInterface {
  marginLeft?: boolean;
}

export const IconContainer = styled('div')<IconContainerInterface>`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: ${(p) => p.marginLeft && '8px'};
`;

export const CurveMenu = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const CurveSwitchWrapper = styled('div')`
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
`;

export const CurveRadioContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
`;

export const CurveRadioWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

export const MobileOverviewImageWrapper = styled('div')`
  display: flex;
  width: 100%;
`;

export const MobileOverviewImage = styled('img')`
  margin: auto;
  margin-top: ${(p) => p.theme.spacing(1)};
  margin-bottom: ${(p) => p.theme.spacing(1)};
  max-height: 200px;
`;

export const Line = styled('div')`
  width: 100%;
  border: 1px solid #eeeeee;
  border-radius: 1px;
  margin: ${(p) => p.theme.spacing(0.5)} 0px;
`;

/* MATERIAL UI OVERRIDES  */
export const TableImage = styled('img')`
  width: 30px;
  height: 40px;
`;

export const CustomImage = styled('img')`
  width: 160px;
`;
